/**
 * M5-A versioned save schema.
 *
 * Permanent hub progress vs provisional run state. Settlement awards XP/loot
 * only via settleRun() — never mid-combat. Curves / completion XP amounts are
 * OPEN placeholders (see SETTLEMENT_REWARD_CONFIG).
 *
 * Vanguard tree (M6-A): class level cap 60, earnedPoints from levels,
 * reservedAchievementPoints held at 0 until designed. Talent ranks persist on
 * ClassProgress.talentRanks. Combat effect application is M6-B (deferred).
 * Do not grant achievement points until designed.
 */

import {
  createEmptyEquippedSlots,
  type EquipmentSlotId,
  type ItemRarity,
} from '../data/equipment';
import type { AffixRoll } from '../data/lootTables';
import type { EquippedSlots, ItemInstance } from '../domain/equipment';
import type { DungeonRunState } from '../domain/dungeon';
import { STARTING_UNLOCKED_DICE_SLOTS } from '../domain/types';

/** Current on-disk schema version (v1 = Milestone 4 SavedGame). */
export const SAVE_SCHEMA_VERSION = 4;

export type { EquipmentSlotId, EquippedSlots, ItemInstance, ItemRarity };

/** Supported class ids. Only vanguard exists in the prototype. */
export type ClassId = 'vanguard';

/** General (shared) progression level cap — confirmed M5 brief. */
export const GENERAL_LEVEL_CAP = 50;

/**
 * Max General talent points from levelling (levels 2–50 → 49 points).
 * Same level−1 pattern as class. Milestone schedule OPEN — see reserved budget.
 */
export const GENERAL_LEVEL_EARNED_POINTS_CAP = 49;

/**
 * Reserved General milestone-point budget so max total can reach 61
 * (49 level + 12 milestone). Schedule not documented — grant 0 until designed.
 */
export const GENERAL_MILESTONE_POINTS_RESERVED = 12;

/** Hard cap on total General talent points once milestones exist. */
export const GENERAL_MAX_TOTAL_POINTS = 61;

/** Per-class level cap — VANGUARD_FINAL_SPEC / M5 brief. */
export const CLASS_LEVEL_CAP = 60;

/**
 * Max class talent points from levelling (levels 2–60 → 59 points).
 * Achievement points (up to 10) are reserved separately and stay at 0 for now.
 */
export const CLASS_LEVEL_EARNED_POINTS_CAP = 59;

/** Reserved achievement point budget — do not grant until designed. */
export const CLASS_ACHIEVEMENT_POINTS_RESERVED = 10;

export type RunLifecycleStatus = 'active' | 'awaiting_settlement' | 'settled';

/** End-of-run outcomes for permanent settlement (M5 reward table). */
export type SettlementOutcome = 'victory' | 'death' | 'abandon';

export interface PlayerProfile {
  /** Optional display name; null until the player sets one. */
  displayName: string | null;
  activeClassId: ClassId;
}

export interface XpLevelProgress {
  xp: number;
  level: number;
}

/**
 * Shared General progression + talent tree (account-wide).
 * Independent from ClassProgress / Vanguard points.
 */
export interface GeneralProgress extends XpLevelProgress {
  /** Talent points earned from General levels (and future milestones). */
  earnedPoints: number;
  /** Points spent across General tree nodes. */
  spentPoints: number;
  /**
   * Reserved milestone budget tracking (always 0 granted for now).
   * Field present so max-total 61 is expressible without inventing a schedule.
   */
  reservedMilestonePoints: number;
  /** Purchased General talent ranks by node id. */
  talentRanks: Record<string, number>;
}

export interface ClassProgress extends XpLevelProgress {
  /**
   * Talent points earned from class levels (not yet spent in a tree).
   * OPEN: exact level→point curve; default is level - 1 clamped to cap.
   */
  earnedPoints: number;
  /**
   * Points spent across class tree nodes.
   * Must equal the sum of values in talentRanks after any purchase/respec.
   */
  spentPoints: number;
  /**
   * Reserved achievement-point budget tracking (always 0 granted for now).
   * Field present for forward compatibility with VANGUARD_FINAL_SPEC.
   */
  reservedAchievementPoints: number;
  /**
   * Purchased Vanguard talent ranks by node id.
   * Missing keys mean 0 ranks. Unknown ids are dropped on migrate/normalize.
   */
  talentRanks: Record<string, number>;
}

/**
 * @deprecated M7-A — use ItemInstance. Kept for typing legacy blobs during migrate.
 * Legacy `{ id, name }` rows are dropped on normalize (empty collection).
 */
export interface OwnedEquipment {
  id: string;
  name: string;
  acquiredAt: number;
  source?: string;
}

/**
 * Provisional loot on a run — banked into permanent ItemInstance collection
 * only on victory settlement. Requires a known defId (no fabricated defs).
 */
export interface ProvisionalLootItem {
  /** Becomes ItemInstance.instanceId when banked. */
  instanceId: string;
  defId: string;
  rarity?: ItemRarity;
  source?: string;
  affixes?: AffixRoll[];
  itemLevel?: number;
  /**
   * Legacy alias accepted by normalize/addProvisionalLoot for older tests.
   * Prefer instanceId.
   */
  id?: string;
  /** Legacy display name — ignored when banking (name comes from def). */
  name?: string;
}

export interface CurrencyWallet {
  gold: number;
  materials: number;
}

export interface DungeonCompletionRecord {
  clearCount: number;
  lastClearedAt: number | null;
}

/**
 * Permanent, hub-scoped progress. Survives run end / reload.
 * Never overwritten by a failed or abandoned run's provisional fields.
 */
export interface PermanentSave {
  profile: PlayerProfile;
  general: GeneralProgress;
  classes: Record<ClassId, ClassProgress>;
  /**
   * Authoritative permanent equipment collection (shared across classes).
   * No capacity limit. Instances use unique instanceIds; defs live in data/.
   */
  equipment: ItemInstance[];
  /**
   * Equipped slot → instanceId refs into `equipment`.
   * Invalid refs are cleared on migrate/normalize (items never deleted for this).
   */
  equipped: EquippedSlots;
  currency: CurrencyWallet;
  dungeonCompletions: Record<string, DungeonCompletionRecord>;
  /**
   * Shared standard dice unlock count (1–6).
   * Derived from General Additional Standard Dice Slots talent on purchase;
   * also writable by DEV / legacy settings for tests.
   */
  unlockedDiceSlots: number;
  /**
   * Special Die slot unlocked via General talent (replaces a standard die;
   * does not add a seventh). Equipment Special Die combat effects deferred (M7-C).
   */
  specialDieSlotUnlocked: boolean;
}

/**
 * What was applied when a run was settled. Used for idempotency audits.
 */
export interface SettlementApplied {
  generalXp: number;
  classXp: number;
  lootItemIds: string[];
  currencyGold: number;
  currencyMaterials: number;
  dungeonId: string;
  outcome: SettlementOutcome;
}

export interface RunSettlementMarker {
  outcome: SettlementOutcome;
  /** Stable id for this settlement event (runId + outcome). */
  settlementId: string;
  settledAt: number;
  applied: SettlementApplied;
}

/**
 * Active or recently-finished dungeon run, including provisional awards
 * and the existing Milestone 4 combat/dungeon payload.
 */
export interface RunState {
  runId: string;
  dungeonId: string;
  status: RunLifecycleStatus;
  /**
   * Provisional permanent Class XP earned from combat this run.
   * Distinct from TemporaryProgress (mid-run skill offers).
   * M5-B wires combat → this field; M5-A provides storage + accumulate API.
   */
  provisionalClassXp: number;
  /** Provisional equipment; lost on death/abandon; banked on victory. */
  provisionalLoot: ProvisionalLootItem[];
  /** Null until settleRun succeeds. */
  settlement: RunSettlementMarker | null;
  /** Existing dungeon + encounter combat state (unchanged combat loop). */
  dungeon: DungeonRunState;
}

/**
 * Root serialisable save document (schema v2).
 */
export interface SavedGame {
  version: number;
  savedAt: number;
  /** Seeded RNG state — required while a run is active. */
  rngState: number;
  permanent: PermanentSave;
  /** Null when no run is in progress (hub-only / post-clear). */
  run: RunState | null;
}

/** Milestone 4 on-disk shape (pre-migration). */
export interface SavedGameV1 {
  version: 1;
  savedAt?: number;
  rngState: number;
  dungeon: DungeonRunState;
  permanent?: { unlockedDiceSlots?: number };
}

/**
 * Visibly configurable settlement placeholders.
 * OPEN: exact XP curves and completion amounts — do not treat as final balance.
 */
export interface SettlementRewardConfig {
  /**
   * General XP granted on successful dungeon completion only.
   * Difficulty must NEVER multiply this (M5 confirmed).
   */
  victoryGeneralXpByDungeonId: Record<string, number>;
  /** Fallback when dungeon id is missing from the map. */
  defaultVictoryGeneralXp: number;
  /**
   * When true, victory also banks run gold/materials into permanent.currency.
   * Existing M4 placeholders; economy TBD.
   */
  bankRunCurrencyOnVictory: boolean;
}

export const SETTLEMENT_REWARD_CONFIG: SettlementRewardConfig = {
  // OPEN / configurable placeholder — Proto Cave completion General XP.
  victoryGeneralXpByDungeonId: {
    proto_cave: 25,
  },
  defaultVictoryGeneralXp: 20,
  bankRunCurrencyOnVictory: true,
};

export function createDefaultClassProgress(): ClassProgress {
  return {
    xp: 0,
    level: 1,
    earnedPoints: 0,
    spentPoints: 0,
    reservedAchievementPoints: 0,
    talentRanks: {},
  };
}

export function createDefaultGeneralProgress(): GeneralProgress {
  return {
    xp: 0,
    level: 1,
    earnedPoints: 0,
    spentPoints: 0,
    reservedMilestonePoints: 0,
    talentRanks: {},
  };
}

export function createDefaultPermanentSave(
  options: {
    unlockedDiceSlots?: number;
    activeClassId?: ClassId;
    specialDieSlotUnlocked?: boolean;
  } = {},
): PermanentSave {
  const unlockedDiceSlots =
    options.unlockedDiceSlots ?? STARTING_UNLOCKED_DICE_SLOTS;
  return {
    profile: {
      displayName: null,
      activeClassId: options.activeClassId ?? 'vanguard',
    },
    general: createDefaultGeneralProgress(),
    classes: {
      vanguard: createDefaultClassProgress(),
    },
    equipment: [],
    equipped: createEmptyEquippedSlots(),
    currency: { gold: 0, materials: 0 },
    dungeonCompletions: {},
    unlockedDiceSlots,
    specialDieSlotUnlocked: options.specialDieSlotUnlocked ?? false,
  };
}

/** View PermanentSave equipment fields as InventoryState. */
export function inventoryFromPermanent(permanent: PermanentSave): {
  items: ItemInstance[];
  equipped: EquippedSlots;
} {
  return {
    items: permanent.equipment,
    equipped: permanent.equipped ?? createEmptyEquippedSlots(),
  };
}

export function applyInventoryToPermanent(
  permanent: PermanentSave,
  inventory: { items: ItemInstance[]; equipped: EquippedSlots },
): PermanentSave {
  return {
    ...permanent,
    equipment: inventory.items,
    equipped: inventory.equipped,
  };
}

export function newRunId(seed?: number): string {
  const t = Date.now().toString(36);
  const s = (seed ?? Math.floor(Math.random() * 1e9)).toString(36);
  return `run_${t}_${s}`;
}

/**
 * Wraps an existing DungeonRunState into RunState without mutating combat fields.
 */
export function wrapDungeonAsRun(
  dungeon: DungeonRunState,
  options: {
    runId?: string;
    status?: RunLifecycleStatus;
    provisionalClassXp?: number;
    provisionalLoot?: ProvisionalLootItem[];
  } = {},
): RunState {
  let status = options.status;
  if (!status) {
    if (dungeon.outcome === 'ongoing') {
      status = 'active';
    } else if (dungeon.phase === 'results') {
      status = 'awaiting_settlement';
    } else {
      status = 'active';
    }
  }
  return {
    runId: options.runId ?? newRunId(dungeon.seed),
    dungeonId: dungeon.dungeonId,
    status,
    provisionalClassXp: options.provisionalClassXp ?? 0,
    provisionalLoot: options.provisionalLoot ? [...options.provisionalLoot] : [],
    settlement: null,
    dungeon,
  };
}

/** Sync M4 stub mirror on the dungeon payload from canonical permanent. */
export function syncDungeonPermanentMirror(
  dungeon: DungeonRunState,
  permanent: PermanentSave,
): DungeonRunState {
  return {
    ...dungeon,
    unlockedDiceSlots: permanent.unlockedDiceSlots,
    permanent: { unlockedDiceSlots: permanent.unlockedDiceSlots },
  };
}
